import React, {
  lazy,
  Suspense,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ErrorNotification from "../../components/ErrorNotification/ErrorNotification";

import WorkspaceLayout from "../../components/WorkspaceLayout/WorkspaceLayout";
import WorkspaceTabs from "../../components/WorkspaceLayout/WorkspaceTabs";
import NextBestActionCard from "../../components/WorkspaceLayout/NextBestActionCard";

import { getOffer } from "../../services/offersService";
import {
  safeDecodeDeep,
  normalizeReturnTo,
  withQuery,
  ROUTES,
} from "../../routes/appRoutes";
import { isFeatureEnabled } from "../../app/featureFlags";

import { normalizeOfferSnapshot } from "./offerViewModel";
import { buildOfferNextBestAction } from "./offerRecommendations";

import "../Shared/marketing-theme.css";
import "./offerLayout.css";

const OfferOverview = lazy(() => import("./OfferOverview"));
const OfferInvitesPage = lazy(() => import("./OfferInvitesPage"));
const Redemptions = lazy(() => import("./Redemptions"));
const OfferRoi = lazy(() => import("./OfferRoi"));

const norm = (value) => (value == null ? "" : String(value).trim());
const isPlaceholder = (value) => norm(value).startsWith(":");

const normalizeId = (raw) => {
  const s0 = norm(raw);
  if (!s0 || isPlaceholder(s0)) return "";
  const s = norm(safeDecodeDeep(s0));
  return s && !isPlaceholder(s) ? s : "";
};


const normaliseJoinedWords = (value) =>
  norm(value)
    .replace(/\bwanting(?=an\b)/gi, "wanting ")
    .replace(/\bfor(?=a\b)/gi, "for ")
    .replace(/\bwith(?=a\b)/gi, "with ")
    .replace(/\s+/g, " ")
    .trim();

const sentenceCaseDisplay = (value) => {
  const cleaned = normaliseJoinedWords(value);
  if (!cleaned) return "";
  return cleaned
    .split(" ")
    .map((word, index) => {
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (/^[A-Z0-9]{2,}$/.test(word) || /\d/.test(word) || /[–-]/.test(word)) return word;
      if (["LocReach", "London", "Instagram", "TikTok", "WhatsApp", "Google"].includes(word)) return word;
      return word.toLowerCase();
    })
    .join(" ");
};

const toFiniteNumberOr = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toIsoDateOrEmpty = (value) => {
  const s = norm(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return "";

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const d = new Date(Date.UTC(year, month - 1, day));
  const isValid =
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day;

  return isValid ? s : "";
};

function mergeOfferSnapshots(offer, overviewSnapshot) {
  const base = normalizeOfferSnapshot(offer, null);
  const summary =
    overviewSnapshot && typeof overviewSnapshot === "object"
      ? overviewSnapshot
      : null;

  if (!summary) {
    return {
      ...base,
      startsAt: base.startsAt || base.starts_at || offer?.startsAt || offer?.starts_at || "",
      expiresAt: base.expiresAt || base.expires_at || offer?.expiresAt || offer?.expires_at || "",
      isOngoing: Boolean(base.isOngoing ?? base.is_ongoing ?? offer?.isOngoing ?? offer?.is_ongoing),
      status: norm(base.status) || norm(offer?.status) || "",
      spendValue: toFiniteNumberOr(
        base.spendValue ?? base.spend ?? base.cost,
        0
      ),
      roiValue: Number.isFinite(
        Number(base.roiValue ?? base.roiMultiple ?? base.roi)
      )
        ? Number(base.roiValue ?? base.roiMultiple ?? base.roi)
        : null,
      acceptedInviteCount: toFiniteNumberOr(base.acceptedInviteCount, 0),
      onboardingSubmittedCount: toFiniteNumberOr(
        base.onboardingSubmittedCount,
        0
      ),
      leadCount: toFiniteNumberOr(base.leadCount ?? base.leads ?? offer?.leadCount ?? offer?.leads, 0),
      bookingRequestCount: toFiniteNumberOr(
        base.bookingRequestCount ?? base.bookingRequests ?? offer?.bookingRequestCount ?? offer?.bookingRequests,
        0,
      ),
      confirmedBookingCount: toFiniteNumberOr(
        base.confirmedBookingCount ?? base.confirmedBookings ?? offer?.confirmedBookingCount ?? offer?.confirmedBookings,
        0,
      ),
      completedBookingCount: toFiniteNumberOr(
        base.completedBookingCount ?? base.completedBookings ?? offer?.completedBookingCount ?? offer?.completedBookings,
        0,
      ),
      noShowBookingCount: toFiniteNumberOr(
        base.noShowBookingCount ??
          base.noShowBookings ??
          offer?.noShowBookingCount ??
          offer?.noShowBookings,
        0,
      ),
    };
  }

  const mergedRoiValueRaw =
    summary.roiValue ??
    summary.roiMultiple ??
    summary.roi ??
    base.roiValue ??
    base.roiMultiple ??
    base.roi;

  return {
    ...base,
    ...summary,
    title: norm(summary.title) || base.title || "Promotion",
    code: norm(summary.code) || base.code || "",
    active:
      typeof summary.active === "boolean" ? summary.active : base.active,
    updatedAt: summary.updatedAt || base.updatedAt || "",
    inviteCount: toFiniteNumberOr(summary.inviteCount, base.inviteCount),
    activeInviteCount: toFiniteNumberOr(
      summary.activeInviteCount,
      base.activeInviteCount
    ),
    acceptedInviteCount: toFiniteNumberOr(
      summary.acceptedInviteCount,
      base.acceptedInviteCount
    ),
    onboardingSubmittedCount: toFiniteNumberOr(
      summary.onboardingSubmittedCount,
      base.onboardingSubmittedCount
    ),
    leadCount: toFiniteNumberOr(
      summary.leadCount ?? summary.leads,
      base.leadCount ?? base.leads,
    ),
    bookingRequestCount: toFiniteNumberOr(
      summary.bookingRequestCount ?? summary.bookingRequests,
      base.bookingRequestCount ?? base.bookingRequests,
    ),
    confirmedBookingCount: toFiniteNumberOr(
      summary.confirmedBookingCount ?? summary.confirmedBookings,
      base.confirmedBookingCount ?? base.confirmedBookings,
    ),
    completedBookingCount: toFiniteNumberOr(
      summary.completedBookingCount ?? summary.completedBookings,
      base.completedBookingCount ?? base.completedBookings,
    ),
    noShowBookingCount: toFiniteNumberOr(
      summary.noShowBookingCount ?? summary.noShowBookings,
      base.noShowBookingCount ?? base.noShowBookings,
    ),
    recordedRevenueValue: toFiniteNumberOr(
      summary.recordedRevenue ??
        summary.recorded_revenue ??
        summary.recordedRevenueValue,
      base.recordedRevenueValue ?? base.recordedRevenue,
    ),
    estimatedBookingRevenueValue: toFiniteNumberOr(
      summary.estimatedBookingRevenue ??
        summary.estimated_booking_revenue ??
        summary.estimatedBookingRevenueValue,
      base.estimatedBookingRevenueValue ?? base.estimatedBookingRevenue,
    ),
    redemptionCount: toFiniteNumberOr(
      summary.redemptionCount,
      base.redemptionCount
    ),
    clicksCount: toFiniteNumberOr(summary.clicksCount, base.clicksCount),
    revenueValue: toFiniteNumberOr(summary.revenueValue, base.revenueValue),
    unattributedCount: toFiniteNumberOr(
      summary.unattributedCount,
      base.unattributedCount
    ),
    topInviteId: norm(summary.topInviteId) || base.topInviteId || "",
    topInviteRedemptions: toFiniteNumberOr(
      summary.topInviteRedemptions,
      base.topInviteRedemptions
    ),
    currency: norm(summary.currency) || base.currency || "GBP",
    discountType: norm(summary.discountType) || base.discountType || "",
    discountText: norm(summary.discountText) || base.discountText || "",
    landingUrl: norm(summary.landingUrl) || base.landingUrl || "",
    spendValue: toFiniteNumberOr(
      summary.spendValue ?? summary.spend ?? summary.cost,
      toFiniteNumberOr(base.spendValue ?? base.spend ?? base.cost, 0)
    ),
    roiValue: Number.isFinite(Number(mergedRoiValueRaw))
      ? Number(mergedRoiValueRaw)
      : null,
  };
}

const safeDateLabel = (value) => {
  const t = new Date(value || 0).getTime();
  if (!Number.isFinite(t) || t <= 0) return "—";

  return new Date(t).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeDateOnlyLabel = (value) => {
  const t = new Date(value || 0).getTime();
  if (!Number.isFinite(t) || t <= 0) return "";

  return new Date(t).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isPastDate = (value) => {
  const t = new Date(value || 0).getTime();
  return Boolean(Number.isFinite(t) && t > 0 && t < Date.now());
};

const offerTimingStatus = (snapshot = {}) => {
  const rawStatus = norm(snapshot.status).toLowerCase();
  const expiresAt = snapshot.expiresAt || snapshot.expires_at;
  const isOngoing = Boolean(snapshot.isOngoing || snapshot.is_ongoing);

  if (rawStatus === "paused" || snapshot.active === false) {
    return { label: "Paused", tone: "warn", timing: "Not currently shareable" };
  }
  if (rawStatus === "expired" || isPastDate(expiresAt)) {
    const ended = safeDateOnlyLabel(expiresAt);
    return { label: "Expired", tone: "warn", timing: ended ? `Ended ${ended}` : "Ended" };
  }
  if (isOngoing || !expiresAt) {
    return { label: "Active", tone: "ok", timing: "Ongoing" };
  }

  const endLabel = safeDateOnlyLabel(expiresAt);
  return { label: "Active", tone: "ok", timing: endLabel ? `Ends ${endLabel}` : "Active" };
};

async function safeCopy(text) {
  const t = norm(text);
  if (!t) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(t);
      return true;
    }
  } catch {
    // ignore
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = t;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Boolean(ok);
  } catch {
    return false;
  }
}

function useToast() {
  const [toast, setToast] = useState("");
  const timer = useRef(null);

  const show = useCallback((msg, ms = 1400) => {
    setToast(String(msg || ""));
    const w = typeof window !== "undefined" ? window : null;
    if (!w) return;

    if (timer.current) w.clearTimeout(timer.current);
    timer.current = w.setTimeout(() => setToast(""), ms);
  }, []);

  useEffect(() => {
    const w = typeof window !== "undefined" ? window : null;
    return () => {
      if (w && timer.current) w.clearTimeout(timer.current);
      timer.current = null;
    };
  }, []);

  return { toast, show };
}

function buildSharedQuery({
  campaignId,
  returnTo,
  by,
  range,
  includeRoi = false,
  refresh = "",
  from = "",
  to = "",
}) {
  const q = {};
  if (campaignId) q.campaignId = campaignId;
  if (returnTo) q.returnTo = returnTo;
  if (refresh) q.refresh = refresh;
  if (from) {
    q.from = from;
    q.fromDay = from;
  }
  if (to) {
    q.to = to;
    q.toDay = to;
  }
  if (includeRoi && by) q.by = by;
  if (includeRoi && range) q.range = range;
  return q;
}

function resolveTabFromPath(pathname, offerId) {
  const rawOfferId = norm(offerId);
  if (!rawOfferId) return "overview";

  const encodedId = encodeURIComponent(rawOfferId);
  const base = `/attribution/offers/${encodedId}`;
  const decodedBase = `/attribution/offers/${rawOfferId}`;
  const path = norm(pathname);

  const suffix = path.startsWith(base)
    ? path.slice(base.length)
    : path.startsWith(decodedBase)
      ? path.slice(decodedBase.length)
      : "";

  if (suffix === "/invites") return "invites";
  if (suffix === "/redemptions") return "redemptions";
  if (suffix === "/roi") return "roi";

  return "overview";
}

const firstStrategyText = (...values) => {
  for (const value of values) {
    const text = norm(value);
    if (text) return text;
  }

  return "";
};

const firstStrategyNumber = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;

    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }

  return 0;
};

const normalizeOfferStrategy = (offer = {}, routeState = {}) => {
  const stateOfferStrategy =
    routeState?.offerStrategy && typeof routeState.offerStrategy === "object"
      ? routeState.offerStrategy
      : {};

  const stateCampaignStrategy =
    routeState?.campaignStrategy && typeof routeState.campaignStrategy === "object"
      ? routeState.campaignStrategy
      : {};

  const nestedOfferStrategy =
    offer?.offerStrategy && typeof offer.offerStrategy === "object"
      ? offer.offerStrategy
      : offer?.offer_strategy && typeof offer.offer_strategy === "object"
        ? offer.offer_strategy
        : {};

  const strategy = {
    source: firstStrategyText(
      stateOfferStrategy.source,
      stateCampaignStrategy.source,
      nestedOfferStrategy.source,
      offer?.campaignSource,
      offer?.campaign_source
    ),
    suggestionId: firstStrategyText(
      stateOfferStrategy.suggestionId,
      stateOfferStrategy.suggestion_id,
      stateCampaignStrategy.suggestionId,
      stateCampaignStrategy.suggestion_id,
      nestedOfferStrategy.suggestionId,
      nestedOfferStrategy.suggestion_id,
      offer?.selectedDemandSuggestionId,
      offer?.selected_demand_suggestion_id
    ),
    businessName: firstStrategyText(
      stateOfferStrategy.businessName,
      stateOfferStrategy.business_name,
      stateCampaignStrategy.businessName,
      stateCampaignStrategy.business_name,
      nestedOfferStrategy.businessName,
      nestedOfferStrategy.business_name,
      offer?.businessName,
      offer?.business_name
    ),
    businessType: firstStrategyText(
      stateOfferStrategy.businessType,
      stateOfferStrategy.business_type,
      stateCampaignStrategy.businessType,
      stateCampaignStrategy.business_type,
      nestedOfferStrategy.businessType,
      nestedOfferStrategy.business_type,
      offer?.businessType,
      offer?.business_type
    ),
    city: firstStrategyText(
      stateOfferStrategy.city,
      stateCampaignStrategy.city,
      nestedOfferStrategy.city,
      offer?.city
    ),
    postcode: firstStrategyText(
      stateOfferStrategy.postcode,
      stateCampaignStrategy.postcode,
      nestedOfferStrategy.postcode,
      offer?.postcode
    ),
    productOrService: firstStrategyText(
      stateOfferStrategy.productOrService,
      stateOfferStrategy.product_or_service,
      stateCampaignStrategy.productOrService,
      stateCampaignStrategy.product_or_service,
      nestedOfferStrategy.productOrService,
      nestedOfferStrategy.product_or_service,
      offer?.productOrService,
      offer?.product_or_service
    ),
    targetCustomer: firstStrategyText(
      stateOfferStrategy.targetCustomer,
      stateOfferStrategy.target_customer,
      stateCampaignStrategy.targetCustomer,
      stateCampaignStrategy.target_customer,
      nestedOfferStrategy.targetCustomer,
      nestedOfferStrategy.target_customer,
      offer?.targetCustomer,
      offer?.target_customer
    ),
    campaignDescription: firstStrategyText(
      stateOfferStrategy.campaignDescription,
      stateOfferStrategy.campaign_description,
      stateCampaignStrategy.campaignDescription,
      stateCampaignStrategy.campaign_description,
      nestedOfferStrategy.campaignDescription,
      nestedOfferStrategy.campaign_description,
      offer?.campaignDescription,
      offer?.campaign_description
    ),
    offerIdea: firstStrategyText(
      stateOfferStrategy.offerIdea,
      stateOfferStrategy.offer_idea,
      stateCampaignStrategy.offerIdea,
      stateCampaignStrategy.offer_idea,
      nestedOfferStrategy.offerIdea,
      nestedOfferStrategy.offer_idea,
      offer?.offerIdea,
      offer?.offer_idea
    ),
    creatorType: firstStrategyText(
      stateOfferStrategy.creatorType,
      stateOfferStrategy.creator_type,
      stateCampaignStrategy.creatorType,
      stateCampaignStrategy.creator_type,
      nestedOfferStrategy.creatorType,
      nestedOfferStrategy.creator_type,
      offer?.creatorType,
      offer?.creator_type
    ),
    creatorCategory: firstStrategyText(
      stateOfferStrategy.creatorCategory,
      stateOfferStrategy.creator_category,
      stateCampaignStrategy.creatorCategory,
      stateCampaignStrategy.creator_category,
      nestedOfferStrategy.creatorCategory,
      nestedOfferStrategy.creator_category,
      offer?.creatorCategory,
      offer?.creator_category
    ),
    localAngle: firstStrategyText(
      stateOfferStrategy.localAngle,
      stateOfferStrategy.local_angle,
      stateCampaignStrategy.localAngle,
      stateCampaignStrategy.local_angle,
      nestedOfferStrategy.localAngle,
      nestedOfferStrategy.local_angle,
      offer?.localAngle,
      offer?.local_angle
    ),
    confidence: firstStrategyText(
      stateOfferStrategy.confidence,
      stateOfferStrategy.suggestionConfidence,
      stateOfferStrategy.suggestion_confidence,
      stateCampaignStrategy.confidence,
      stateCampaignStrategy.suggestionConfidence,
      stateCampaignStrategy.suggestion_confidence,
      nestedOfferStrategy.confidence,
      nestedOfferStrategy.suggestionConfidence,
      nestedOfferStrategy.suggestion_confidence,
      offer?.suggestionConfidence,
      offer?.suggestion_confidence
    ),
    radiusMiles: firstStrategyNumber(
      stateOfferStrategy.radiusMiles,
      stateOfferStrategy.radius_miles,
      stateCampaignStrategy.radiusMiles,
      stateCampaignStrategy.radius_miles,
      nestedOfferStrategy.radiusMiles,
      nestedOfferStrategy.radius_miles,
      offer?.radiusMiles,
      offer?.radius_miles
    ),
  };

  if (!strategy.source && (strategy.suggestionId || strategy.offerIdea)) {
    strategy.source = "ai_demand_suggestion";
  }

  return Object.fromEntries(
    Object.entries(strategy).filter(([, value]) => {
      if (typeof value === "number") return value > 0;
      return norm(value).length > 0;
    })
  );
};

const hasOfferStrategyValue = (strategy = {}) =>
  Boolean(
    strategy?.offerIdea ||
      strategy?.creatorType ||
      strategy?.localAngle ||
      strategy?.targetCustomer ||
      strategy?.productOrService ||
      strategy?.campaignDescription
  );

function OfferStrategyInsightCard({ strategy, invitesHref = "" }) {
  if (!hasOfferStrategyValue(strategy)) return null;

  const chips = [
    strategy.businessType ? `Business: ${sentenceCaseDisplay(strategy.businessType)}` : "",
    strategy.city || strategy.postcode
      ? `Area: ${strategy.city || strategy.postcode}`
      : "",
    strategy.productOrService ? `Promotion focus: ${sentenceCaseDisplay(strategy.productOrService)}` : "",
  ].filter(Boolean);

  const rows = [
    ["Suitable creators", sentenceCaseDisplay(strategy.creatorType)],
    ["Why this could work", sentenceCaseDisplay(strategy.localAngle)],
    ["Customers to reach", sentenceCaseDisplay(strategy.targetCustomer)],
    ["Customer offer", sentenceCaseDisplay(strategy.offerIdea || strategy.campaignDescription)],
  ].filter(([, value]) => norm(value));

  return (
    <section className="card ol-strategyCard" aria-label="Promotion strategy">
      <div className="card-body ol-strategyCardBody">
        <div className="ol-strategyHead">
          <div>
            <div className="ol-strategyEyebrow">Promotion plan</div>
            <h3 className="ol-strategyTitle">Keep creator outreach aligned with the customer opportunity</h3>
            <p className="ol-strategyDescription">
              Use this plan when selecting creators, preparing messages and reviewing customer results.
            </p>
          </div>

          {invitesHref ? (
            <Link className="btn btn-primary btn-sm" to={invitesHref}>
              Add creators
            </Link>
          ) : null}
        </div>

        {chips.length ? (
          <div className="ol-strategyChips" aria-label="Promotion strategy context">
            {chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
        ) : null}

        {rows.length ? (
          <div className="ol-strategyGrid">
            {rows.map(([label, value]) => (
              <div key={label} className="ol-strategyItem">
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}



function OfferTabSkeleton({ activeTab = "overview" }) {
  const isRoi = activeTab === "roi";
  const isInvites = activeTab === "invites";
  const isRedemptions = activeTab === "redemptions";

  return (
    <div
      className={`ol-tabSkeleton ${
        isRoi ? "is-roi" : isInvites ? "is-invites" : isRedemptions ? "is-redemptions" : "is-overview"
      }`.trim()}
      aria-hidden="true"
    >
      <div className="ol-skeletonPanel">
        <div className="ol-skeletonLine ol-skeletonLine--short" />
        <div className="ol-skeletonLine ol-skeletonLine--title" />
        <div className="ol-skeletonLine ol-skeletonLine--body" />

        <div className="ol-skeletonGrid">
          <span />
          <span />
          <span />
          <span />
          {isRoi ? <span /> : null}
        </div>

        <div className="ol-skeletonWide" />
      </div>
    </div>
  );
}

function OfferWorkspaceSkeleton({
  activeTab = "overview",
  backTo = "",
  backLabel = "Promotions",
  embedded = false,
}) {
  if (embedded) {
    return (
      <div className="page marketing ol-page--embedded" aria-busy="true">
        <div
          className={`ol-embed ol-tone--${offerTabTone(activeTab)}`}
          data-offer-tone={offerTabTone(activeTab)}
        >
          <div className="ol-embedTabs" aria-hidden="true">
            <div className="ol-skeletonTabs">
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="ol-content ol-content--embedded">
            <OfferTabSkeleton activeTab={activeTab} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page marketing" aria-busy="true">
      <div className="ol-loadingShell page-shell">
        <div className="ol-loadingInner page-content">
          <header className="ol-loadingHeader">
            {backTo ? (
              <Link className="wl-backLink" to={backTo} aria-label={backLabel}>
                <span aria-hidden="true">←</span>
                <span>{backLabel}</span>
              </Link>
            ) : null}

            <div className="ol-loadingHero" aria-hidden="true">
              <div className="ol-skeletonLine ol-skeletonLine--eyebrow" />
              <div className="ol-skeletonLine ol-skeletonLine--heroTitle" />
              <div className="ol-skeletonLine ol-skeletonLine--heroTitleSmall" />
              <div className="ol-skeletonLine ol-skeletonLine--subtitle" />
              <div className="ol-skeletonMeta">
                <span />
                <span />
                <span />
              </div>
            </div>
          </header>

          <div className="ol-skeletonTabs" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>

          <OfferTabSkeleton activeTab={activeTab} />
        </div>
      </div>
    </div>
  );
}




const offerTabTone = (tab) => {
  if (tab === "invites") return "creator";
  if (tab === "redemptions") return "outcome";
  if (tab === "roi") return "roi";
  return "summary";
};

export default function OfferLayout({
  embedded = false,
  hidePageChrome = false,
  loadOnboardingState = null,
  onOpenOfferWorkspace = null,
  onOpenCreatorCrm = null,
  onOpenPublicInvite = null,
  onOpenOnboarding = null,
}) {
  const { offerId: rawId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast, show } = useToast();

  const attributionEnabled = isFeatureEnabled("attributionEnabled");
  const roiEnabled =
    attributionEnabled && isFeatureEnabled("advancedAnalyticsEnabled");

  const mountedRef = useRef(true);
  const reqSeqRef = useRef(0);
  const activeOfferIdRef = useRef("");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const offerId = useMemo(() => normalizeId(rawId), [rawId]);

  const stateCampaignId = location?.state?.campaignId;
  const stateReturnTo = location?.state?.returnTo;
  const stateFrom = location?.state?.from;
  const stateEmbedded = location?.state?.embedded === true;
  const stateHidePageChrome = location?.state?.hidePageChrome === true;
  const stateRefreshToken =
    location?.state?.refreshAt || location?.state?.invitesUpdatedAt || "";

  const isEmbeddedLayout = Boolean(
    embedded || hidePageChrome || stateEmbedded || stateHidePageChrome
  );

  const campaignId = useMemo(() => {
    const q = normalizeId(searchParams.get("campaignId") || "");
    const st = normalizeId(stateCampaignId || "");
    return q || st;
  }, [searchParams, stateCampaignId]);

  const rawReturnTo = searchParams.get("returnTo") || "";
  const rawFrom = searchParams.get("from") || "";
  const rawRefresh = searchParams.get("refresh") || "";

  const refreshToken = useMemo(
    () => norm(rawRefresh || stateRefreshToken),
    [rawRefresh, stateRefreshToken]
  );

  const rangeFrom = useMemo(
    () =>
      toIsoDateOrEmpty(
        searchParams.get("fromDay") || searchParams.get("from") || ""
      ),
    [searchParams]
  );

  const rangeTo = useMemo(
    () =>
      toIsoDateOrEmpty(
        searchParams.get("toDay") || searchParams.get("to") || ""
      ),
    [searchParams]
  );

  const normalizedReturnTo = useMemo(
    () => normalizeReturnTo(rawReturnTo),
    [rawReturnTo]
  );
  const normalizedLegacyReturnTo = useMemo(
    () => normalizeReturnTo(rawFrom),
    [rawFrom]
  );
  const normalizedStateReturnTo = useMemo(
    () => normalizeReturnTo(norm(stateReturnTo)),
    [stateReturnTo]
  );
  const normalizedStateFrom = useMemo(
    () => normalizeReturnTo(norm(stateFrom)),
    [stateFrom]
  );

  const effectiveReturnTo = useMemo(() => {
    return (
      normalizedReturnTo ||
      normalizedLegacyReturnTo ||
      normalizedStateReturnTo ||
      normalizedStateFrom ||
      ""
    );
  }, [
    normalizedReturnTo,
    normalizedLegacyReturnTo,
    normalizedStateReturnTo,
    normalizedStateFrom,
  ]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (normalizedReturnTo && normalizedReturnTo !== rawReturnTo) {
      next.set("returnTo", normalizedReturnTo);
      changed = true;
    }

    if (!normalizedReturnTo && effectiveReturnTo) {
      next.set("returnTo", effectiveReturnTo);
      changed = true;
    }

    if (rawFrom) {
      next.delete("from");
      changed = true;
    }

    if (refreshToken && refreshToken !== rawRefresh) {
      next.set("refresh", refreshToken);
      changed = true;
    }

    if (!changed) return;

    startTransition(() => {
      setSearchParams(next, { replace: true });
    });
  }, [
    searchParams,
    setSearchParams,
    rawReturnTo,
    rawFrom,
    rawRefresh,
    normalizedReturnTo,
    effectiveReturnTo,
    refreshToken,
  ]);

  const defaultBackUrl = useMemo(() => {
    return campaignId
      ? withQuery(
          ROUTES.campaignDetails(campaignId),
          effectiveReturnTo
            ? { tab: "offers", returnTo: effectiveReturnTo }
            : { tab: "offers" }
        )
      : ROUTES.attributionOffers;
  }, [campaignId, effectiveReturnTo]);

  const backUrl = effectiveReturnTo || defaultBackUrl;

  const allowedTabs = useMemo(() => {
    const items = ["overview"];
    if (attributionEnabled) items.push("invites", "redemptions");
    if (roiEnabled) items.push("roi");
    return items;
  }, [attributionEnabled, roiEnabled]);

  const activeTab = useMemo(() => {
    const pathTab = resolveTabFromPath(location.pathname, offerId);
    if (pathTab !== "overview") {
      return allowedTabs.includes(pathTab) ? pathTab : "overview";
    }

    const queryTab = norm(searchParams.get("tab"));
    return allowedTabs.includes(queryTab) ? queryTab : "overview";
  }, [location.pathname, offerId, searchParams, allowedTabs]);

  const tabTone = offerTabTone(activeTab);
  const tabToneClass = `ol-tone--${tabTone}`;

  const isOverviewTab = activeTab === "overview";
  const isInvitesTab = activeTab === "invites";
  const isRedemptionsTab = activeTab === "redemptions";
  const isRoiTab = activeTab === "roi";
  const isFocusedWorkTab = isInvitesTab || isRedemptionsTab || isRoiTab;

  const roiBy = useMemo(() => {
    const raw = norm(searchParams.get("by"));
    return raw === "invite" ? "invite" : "offer";
  }, [searchParams]);

  const roiRange = useMemo(() => {
    const raw = norm(searchParams.get("range"));
    return raw || "30d";
  }, [searchParams]);

  const [offer, setOffer] = useState(null);
  const [overviewSnapshot, setOverviewSnapshot] = useState(null);
  const [loading, setLoading] = useState(Boolean(offerId));
  const [error, setError] = useState("");

  const currentOffer = useMemo(() => {
    if (!offerId || !offer) return null;

    const loadedOfferId = normalizeId(
      offer?.offerId || offer?.offer_id || offer?.id || offer?._id || "",
    );

    return loadedOfferId === offerId ? offer : null;
  }, [offer, offerId]);

  const routeChangingOffer = Boolean(offerId && offer && !currentOffer);
  const visibleLoading = Boolean(loading || routeChangingOffer);

  useEffect(() => {
    setOverviewSnapshot(null);
  }, [offerId, campaignId, refreshToken]);

  const refresh = useCallback(async () => {
    if (!offerId) return null;

    reqSeqRef.current += 1;
    const mySeq = reqSeqRef.current;

    if (mountedRef.current) {
      setError("");
      setLoading(true);
    }

    try {
      const data = await getOffer(offerId);

      if (!mountedRef.current || mySeq !== reqSeqRef.current) return null;

      activeOfferIdRef.current = offerId;
      setOffer(data || null);
      if (!data) setError("Promotion not found.");
      return data || null;
    } catch (err) {
      if (!mountedRef.current || mySeq !== reqSeqRef.current) return null;
      setOffer(null);
      setError(err?.message || "Failed to load promotion details.");
      return null;
    } finally {
      if (!mountedRef.current || mySeq !== reqSeqRef.current) return null;
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    if (!offerId) {
      activeOfferIdRef.current = "";
      setOffer(null);
      setLoading(false);
      setError("");
      return;
    }

    if (activeOfferIdRef.current !== offerId) {
      activeOfferIdRef.current = offerId;
      setOffer(null);
    }

    setError("");
    setLoading(true);

    const t = setTimeout(() => {
      void refresh();
    }, 0);

    return () => clearTimeout(t);
  }, [offerId, refresh, refreshToken]);

  useEffect(() => {
    if (!offerId) return;

    const onFocus = () => {
      void refresh();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [offerId, refresh]);

  const overviewHref = useMemo(
    () =>
      offerId
        ? withQuery(
            ROUTES.attributionOffer(offerId),
            buildSharedQuery({
              campaignId,
              returnTo: effectiveReturnTo,
              refresh: refreshToken,
              from: rangeFrom,
              to: rangeTo,
            })
          )
        : ROUTES.attributionOffers,
    [offerId, campaignId, effectiveReturnTo, refreshToken, rangeFrom, rangeTo]
  );

  const invitesHref = useMemo(
    () =>
      offerId
        ? withQuery(
            ROUTES.attributionOfferInvites(offerId),
            buildSharedQuery({
              campaignId,
              returnTo: effectiveReturnTo,
              refresh: refreshToken,
              from: rangeFrom,
              to: rangeTo,
            })
          )
        : ROUTES.attributionOffers,
    [offerId, campaignId, effectiveReturnTo, refreshToken, rangeFrom, rangeTo]
  );

  const redemptionsHref = useMemo(
    () =>
      offerId
        ? withQuery(
            ROUTES.attributionOfferRedemptions(offerId),
            buildSharedQuery({
              campaignId,
              returnTo: effectiveReturnTo,
              refresh: refreshToken,
              from: rangeFrom,
              to: rangeTo,
            })
          )
        : ROUTES.attributionOffers,
    [offerId, campaignId, effectiveReturnTo, refreshToken, rangeFrom, rangeTo]
  );

  const bookingsHref = useMemo(
    () =>
      campaignId
        ? withQuery(ROUTES.campaignDetails(campaignId), {
            tab: "bookings",
            returnTo: effectiveReturnTo,
          })
        : "",
    [campaignId, effectiveReturnTo]
  );

  const roiHref = useMemo(
    () =>
      offerId
        ? withQuery(
            ROUTES.attributionOfferRoi(offerId),
            buildSharedQuery({
              campaignId,
              returnTo: effectiveReturnTo,
              by: roiBy,
              range: roiRange,
              includeRoi: true,
              refresh: refreshToken,
              from: rangeFrom,
              to: rangeTo,
            })
          )
        : ROUTES.attributionOffers,
    [
      offerId,
      campaignId,
      effectiveReturnTo,
      roiBy,
      roiRange,
      refreshToken,
      rangeFrom,
      rangeTo,
    ]
  );

  const snapshot = useMemo(
    () => mergeOfferSnapshots(currentOffer, overviewSnapshot),
    [currentOffer, overviewSnapshot]
  );

  const offerStrategy = useMemo(
    () => normalizeOfferStrategy(snapshot || currentOffer || {}, location?.state || {}),
    [snapshot, currentOffer, location?.state]
  );

  const hasStrategy = useMemo(
    () => hasOfferStrategyValue(offerStrategy),
    [offerStrategy]
  );

  const title = currentOffer ? sentenceCaseDisplay(snapshot.title) : "";
  const code = norm(snapshot.code);
  const active = snapshot.active === true;
  const inviteCount = toFiniteNumberOr(snapshot.inviteCount, 0);
  const activeInviteCount = toFiniteNumberOr(snapshot.activeInviteCount, 0);
  const acceptedInviteCount = toFiniteNumberOr(snapshot.acceptedInviteCount, 0);
  const onboardingSubmittedCount = toFiniteNumberOr(
    snapshot.onboardingSubmittedCount,
    0
  );
  const redemptionCount = toFiniteNumberOr(snapshot.redemptionCount, 0);
  const spendValue = toFiniteNumberOr(snapshot.spendValue, 0);
  const revenueValue = toFiniteNumberOr(snapshot.revenueValue, 0);
  const roiValue = Number.isFinite(Number(snapshot.roiValue))
    ? Number(snapshot.roiValue)
    : null;

  const handleCopyCode = useCallback(async () => {
    if (!code) return;
    const ok = await safeCopy(code);
    show(ok ? "Promotion code copied" : "Copy failed", ok ? 1200 : 2000);
  }, [code, show]);

  const handleCopyId = useCallback(async () => {
    if (!offerId) return;
    const ok = await safeCopy(offerId);
    show(ok ? "Support reference copied" : "Copy failed", ok ? 1200 : 2000);
  }, [offerId, show]);

  const bumpRefreshToken = useCallback(() => {
    const nextRefresh = Date.now().toString();

    startTransition(() => {
      const next = new URLSearchParams(searchParams);
      next.set("refresh", nextRefresh);
      setSearchParams(next, { replace: true });
    });

    return nextRefresh;
  }, [searchParams, setSearchParams]);

  const handleWorkspaceRefresh = useCallback(() => {
    setOverviewSnapshot(null);
    bumpRefreshToken();
  }, [bumpRefreshToken]);

  const tabs = useMemo(() => {
    const items = [{ label: "Overview", to: overviewHref, end: true }];

    if (attributionEnabled) {
      items.push(
        { label: "Creators", to: invitesHref },
        { label: "Customer outcomes", to: redemptionsHref }
      );
    }

    if (roiEnabled) {
      items.push({ label: "Results", to: roiHref });
    }

    return items;
  }, [
    overviewHref,
    invitesHref,
    bookingsHref,
    redemptionsHref,
    roiHref,
    attributionEnabled,
    roiEnabled,
  ]);

  const campaignLink = useMemo(() => {
    if (!campaignId) return "";

    return withQuery(
      ROUTES.campaignDetails(campaignId),
      effectiveReturnTo
        ? { tab: "offers", returnTo: effectiveReturnTo }
        : { tab: "offers" }
    );
  }, [campaignId, effectiveReturnTo]);

  const timingStatus = useMemo(() => offerTimingStatus(snapshot), [snapshot]);

  const meta = useMemo(() => {
    const items = [
      {
        label: "Code",
        value: code ? (
          <button
            type="button"
            className="ol-metaInlineBtn"
            onClick={handleCopyCode}
            title="Copy promotion code"
          >
            <span className="code">{code}</span>
          </button>
        ) : (
          "—"
        ),
      },
      ...(hasStrategy
        ? [
            {
              label: "Setup",
              value: <span className="badge badge-primary">Suggested strategy</span>,
            },
          ]
        : []),
      {
        label: "Timing",
        value: timingStatus.timing || "—",
      },
      {
        label: "Updated",
        value: snapshot.updatedAt ? safeDateLabel(snapshot.updatedAt) : "—",
      },
    ];

    return items;
  }, [
    code,
    hasStrategy,
    snapshot.updatedAt,
    timingStatus,
    handleCopyCode,
  ]);

  const statusBadge = useMemo(
    () => (
      <span className={timingStatus.tone === "ok" ? "badge badge-ok" : "badge badge-warn"}>
        {timingStatus.label}
      </span>
    ),
    [timingStatus]
  );

  const layoutMeta = useMemo(
    () => (isFocusedWorkTab ? [] : meta),
    [isFocusedWorkTab, meta]
  );

  const layoutSubtitle = useMemo(() => {
    if (isInvitesTab) {
      return "Creators are optional. Add them only when you want creator-specific tracking links and outreach notes.";
    }
    if (isRedemptionsTab) {
      return "Record completed customer actions and keep tracking accurate.";
    }
    if (isRoiTab) {
      return "Review customer activity, revenue, and ROI for this promotion.";
    }

    if (hasStrategy) {
      return "Manage the customer page, tracked links, optional creators, customer outcomes, and performance from one workspace.";
    }

    return attributionEnabled
      ? "Manage the customer page, tracked links, optional creators, outcomes, and performance for this promotion."
      : "Review promotion details.";
  }, [isInvitesTab, isRedemptionsTab, isRoiTab, attributionEnabled, hasStrategy]);

  const layoutSecondaryActions = useMemo(() => {
    const refreshAction = {
      label: loading ? "Loading…" : "Refresh",
      onClick: handleWorkspaceRefresh,
      disabled: visibleLoading,
      className: "btn btn-ghost",
    };

    if (isFocusedWorkTab) {
      return [
        {
          label: "Back to promotion",
          to: overviewHref,
          className: "btn btn-ghost",
        },
      ];
    }

    return [refreshAction];
  }, [isFocusedWorkTab, visibleLoading, handleWorkspaceRefresh, overviewHref]);

  const handleInviteCreated = useCallback(() => {
    setOverviewSnapshot(null);
    bumpRefreshToken();
  }, [bumpRefreshToken]);

  const handleOverviewSummaryChange = useCallback((summary) => {
    setOverviewSnapshot(summary || null);
  }, []);

  const nextBestAction = useMemo(() => {
    if (!isOverviewTab || !attributionEnabled) return null;

    const recommendation = buildOfferNextBestAction(snapshot, {
      creators: invitesHref,
      bookings: bookingsHref,
      redemptions: redemptionsHref,
      roi: roiHref,
    });

    return recommendation ? (
      <NextBestActionCard
        eyebrow={recommendation.eyebrow}
        title={recommendation.title}
        description={recommendation.description}
        note={recommendation.note}
        primaryAction={recommendation.primaryAction}
        secondaryAction={recommendation.secondaryAction}
      />
    ) : null;
  }, [
    isOverviewTab,
    attributionEnabled,
    snapshot,
    invitesHref,
    redemptionsHref,
    roiHref,
  ]);

  const renderTabContent = () => {
    if (visibleLoading && !currentOffer) {
      return <OfferTabSkeleton activeTab={activeTab} />;
    }

    if (error) {
      return <ErrorNotification message={error} />;
    }

    if (!currentOffer) {
      return <ErrorNotification message="Promotion not found." />;
    }

    if (isInvitesTab) {
      if (!attributionEnabled) {
        return (
          <ErrorNotification message="Attribution features are currently disabled." />
        );
      }

      return (
        <OfferInvitesPage
          offerId={offerId}
          campaignId={campaignId}
          offerTitle={title}
          offerStrategy={offerStrategy}
          returnTo={effectiveReturnTo || backUrl}
          onInvitesChanged={handleInviteCreated}
          loadOnboardingState={loadOnboardingState}
          onOpenOfferWorkspace={onOpenOfferWorkspace}
          onOpenCreatorCrm={onOpenCreatorCrm}
          onOpenPublicInvite={onOpenPublicInvite}
          onOpenOnboarding={onOpenOnboarding}
          embedded
          hidePageChrome
          refreshToken={refreshToken}
        />
      );
    }

    if (isRedemptionsTab) {
      if (!attributionEnabled) {
        return (
          <ErrorNotification message="Attribution features are currently disabled." />
        );
      }

      return (
        <Redemptions
          offerId={offerId}
          campaignId={campaignId}
          completedBookingCount={toFiniteNumberOr(
            snapshot.completedBookingCount,
            0,
          )}
          refreshToken={refreshToken}
          inWorkspace
        />
      );
    }

    if (isRoiTab) {
      if (!roiEnabled) {
        return (
          <ErrorNotification message="ROI analytics are currently disabled for this promotion." />
        );
      }

      return (
        <OfferRoi
          offerId={offerId}
          campaignId={campaignId}
          refreshToken={refreshToken}
          inWorkspace
        />
      );
    }

    return (
      <OfferOverview
        offerId={offerId}
        campaignId={campaignId}
        returnTo={effectiveReturnTo || backUrl}
        offerMeta={currentOffer}
        offerStrategy={offerStrategy}
        inWorkspace
        onSummaryChange={handleOverviewSummaryChange}
        refreshToken={refreshToken}
      />
    );
  };

  if (!offerId) {
    return (
      <div className="page marketing">
        <div className="ol-shell">
          <div className="card">
            <div className="card-body ol-empty">
              <span className="badge badge-warn">Invalid or missing promotion ID</span>
              <div className="ol-spacer" aria-hidden="true" />
              <Link className="btn btn-primary" to={backUrl}>
                Back
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (visibleLoading && !currentOffer && !error) {
    return (
      <OfferWorkspaceSkeleton
        activeTab={activeTab}
        backTo={backUrl}
        backLabel="Promotions"
        embedded={isEmbeddedLayout}
      />
    );
  }

  if (isEmbeddedLayout) {
    return (
      <div
        className="page marketing ol-page--embedded"
        aria-busy={loading ? "true" : "false"}
      >
        <div className={`ol-embed ${isFocusedWorkTab ? "ol-embed--focused" : ""}`}>
          {toast ? (
            <div className="ol-toastInline" role="status" aria-live="polite">
              {toast}
            </div>
          ) : null}

          <div className="ol-embedTabs">
            <WorkspaceTabs items={tabs} />
          </div>

          <div className="ol-content ol-content--embedded">
            <Suspense fallback={<OfferTabSkeleton activeTab={activeTab} />}>
              {renderTabContent()}
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page marketing" aria-busy={loading ? "true" : "false"}>
      <WorkspaceLayout
        showBack
        backTo={backUrl}
        backLabel="Promotions"
        eyebrow="Promotion"
        title={title}
        subtitle={layoutSubtitle}
        status={!isFocusedWorkTab ? statusBadge : null}
        meta={layoutMeta}
        primaryAction={null}
        secondaryActions={layoutSecondaryActions}
        progress={null}
        nextStep={nextBestAction}
        aiInsight={
          isOverviewTab && hasStrategy ? (
            <OfferStrategyInsightCard
              strategy={offerStrategy}
              invitesHref={invitesHref}
            />
          ) : null
        }
        tabs={<WorkspaceTabs items={tabs} />}
        stickyAction={null}
        className={`ol-workspace ${tabToneClass} ${
          isFocusedWorkTab ? "ol-workspace--focused" : ""
        }`.trim()}
      >
        {toast ? (
          <div className="ol-toastInline" role="status" aria-live="polite">
            {toast}
          </div>
        ) : null}

        <div className="ol-content">
          <Suspense fallback={<OfferTabSkeleton activeTab={activeTab} />}>
            {renderTabContent()}
          </Suspense>
        </div>
      </WorkspaceLayout>
    </div>
  );
}
